/**
 * User Form Component
 * 
 * Form for creating and editing users
 * - Zod validation schema
 * - React Hook Form integration
 * - Accessible form controls
 * - Error messages
 * - Loading states
 * 
 * @component
 * @example
 * <UserForm
 *   mode="create"
 *   onSubmit={(data) => createUser(data)}
 *   onCancel={() => setOpen(false)}
 * />
 */

import { FC, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FiAlertCircle } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import { organizationService } from '@/services';
import type { Organization } from '@/services';
import { 
  userFormSchema, 
  userFormCreateSchema,
  type UserFormProps, 
  type UserFormData 
} from './UserForm.types';
import styles from './UserForm.module.css';

const UserForm: FC<UserFormProps> = ({
  initialValues,
  mode,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  // Organization select states
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  
  // Select schema based on mode
  const schema = mode === 'create' ? userFormCreateSchema : userFormSchema;

  // Initialize React Hook Form with Zod resolver
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<UserFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: initialValues?.firstName || '',
      lastName: initialValues?.lastName || '',
      email: initialValues?.email || '',
      organizationId: initialValues?.organizationId,
      status: initialValues?.status || 'active',
      password: '',
    },
  });

  /**
   * Load organizations on mount
   */
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setIsLoadingOrgs(true);
        console.log('🔍 Iniciando carregamento de organizações...');
        
        // Buscar organizações da API
        const response = await organizationService.getAll(
          { page: 0, size: 100, sort: 'name,asc' }
        );
        
        console.log('📋 Response completa:', response);
        console.log('📋 Organizações carregadas:', response.data);
        console.log('📋 Total de organizações:', response.data?.length);
        
        setOrganizations(response.data || []);
      } catch (error) {
        console.error('❌ Erro ao carregar organizações:', error);
      } finally {
        setIsLoadingOrgs(false);
      }
    };

    loadOrganizations();
  }, []);

  // Reset form when initialValues change
  useEffect(() => {
    if (initialValues && organizations.length > 0) {
      reset({
        firstName: initialValues.firstName || '',
        lastName: initialValues.lastName || '',
        email: initialValues.email || '',
        organizationId: initialValues.organizationId,
        status: initialValues.status || 'active',
        password: '',
      });
    }
  }, [initialValues, organizations, reset]);

  /**
   * Handle form submission
   */
  const onSubmitForm = async (data: UserFormData) => {
    await onSubmit(data);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmitForm)} noValidate>
      {/* First Name Field */}
      <div className={styles.formGroup}>
        <label htmlFor="firstName" className={styles.label}>
          Nome <span className={styles.required}>*</span>
        </label>
        <input
          id="firstName"
          type="text"
          className={styles.input}
          {...register('firstName')}
          placeholder="Digite o nome"
          aria-invalid={!!errors.firstName}
          aria-describedby={errors.firstName ? 'firstName-error' : undefined}
          disabled={isLoading}
          autoFocus
        />
        {errors.firstName && (
          <span id="firstName-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.firstName.message}
          </span>
        )}
      </div>

      {/* Last Name Field */}
      <div className={styles.formGroup}>
        <label htmlFor="lastName" className={styles.label}>
          Sobrenome <span className={styles.required}>*</span>
        </label>
        <input
          id="lastName"
          type="text"
          className={styles.input}
          {...register('lastName')}
          placeholder="Digite o sobrenome"
          aria-invalid={!!errors.lastName}
          aria-describedby={errors.lastName ? 'lastName-error' : undefined}
          disabled={isLoading}
        />
        {errors.lastName && (
          <span id="lastName-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.lastName.message}
          </span>
        )}
      </div>

      {/* Email Field */}

      {/* Email Field */}
      <div className={styles.formGroup}>
        <label htmlFor="email" className={styles.label}>
          Email <span className={styles.required}>*</span>
        </label>
        <input
          id="email"
          type="email"
          className={styles.input}
          {...register('email')}
          placeholder="usuario@exemplo.com"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
          disabled={isLoading}
        />
        {errors.email && (
          <span id="email-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.email.message}
          </span>
        )}
      </div>

      {/* Password Field (only for create mode) */}
      {mode === 'create' && (
        <div className={styles.formGroup}>
          <label htmlFor="password" className={styles.label}>
            Senha <span className={styles.required}>*</span>
          </label>
          <input
            id="password"
            type="password"
            className={styles.input}
            {...register('password')}
            placeholder="Mínimo 6 caracteres"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error password-help' : 'password-help'}
            disabled={isLoading}
          />
          {errors.password && (
            <span id="password-error" className={styles.error}>
              <FiAlertCircle size={14} />
              {errors.password.message}
            </span>
          )}
          <span id="password-help" className={styles.helpText}>
            A senha deve ter no mínimo 6 caracteres
          </span>
        </div>
      )}

      {/* Organization Select */}
      <div className={styles.formGroup}>
        <label htmlFor="organizationId" className={styles.label}>
          Organização <span className={styles.required}>*</span>
        </label>
        <Controller
          name="organizationId"
          control={control}
          render={({ field }) => (
            <select
              id="organizationId"
              className={styles.select}
              aria-invalid={!!errors.organizationId}
              {...field}
              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
              value={field.value || ''}
              disabled={isLoading || isLoadingOrgs}
            >
              <option value="">
                {isLoadingOrgs ? 'Carregando organizações...' : 'Selecione uma organização'}
              </option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          )}
        />
        {errors.organizationId && (
          <span id="org-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.organizationId.message}
          </span>
        )}
        <span className={styles.helpText}>
          Selecione a organização à qual o usuário pertence
        </span>
        {!isLoadingOrgs && organizations.length === 0 && (
          <p className={styles.error}>
            Nenhuma organização ativa encontrada. Por favor, crie uma organização primeiro.
          </p>
        )}
      </div>

      {/* Status Field */}
      <div className={styles.formGroup}>
        <label htmlFor="status" className={styles.label}>
          Status <span className={styles.required}>*</span>
        </label>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <select
              id="status"
              className={styles.select}
              {...field}
              disabled={isLoading}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="blocked">Bloqueado</option>
            </select>
          )}
        />
        {errors.status && (
          <span className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.status.message}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
        >
          {mode === 'create' ? 'Criar Usuário' : 'Salvar Alterações'}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
